import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiPhone, FiMail, FiMessageCircle, FiHelpCircle, FiChevronDown, FiSend, FiCheck, FiShield, FiUsers, FiGift, FiSearch } from 'react-icons/fi';
import { HiOutlineBuildingStorefront } from 'react-icons/hi2';
import api from '../api';

const C = {
  rose: '#e3425c', roseLight: 'rgba(227,66,92,0.08)', roseDeep: '#c94d5c',
  gold: '#d4a853', goldLight: 'rgba(212,168,83,0.12)', goldDark: '#b8923e',
  dark: '#1c1c1e', darkLight: '#3a3a3c', cream: '#f7f7f8', creamDark: '#ebebed',
  white: '#ffffff', gray: '#8e8e93', grayLight: '#aeaeb2',
};

export default function HelpSupport() {
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [enquiryForm, setEnquiryForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [enquirySubmitted, setEnquirySubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { api.get('/faqs').then(r => setFaqs(r.data)).catch(() => {}); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!enquiryForm.name || !enquiryForm.email || !enquiryForm.subject || !enquiryForm.message) return;
    setLoading(true);
    try {
      await api.post('/enquiry', enquiryForm);
      setEnquirySubmitted(true);
    } catch { alert('Failed to submit. Please try again.'); }
    setLoading(false);
  };

  const categories = [
    { key: 'all', label: 'All Topics', icon: <FiHelpCircle size={14} />, color: C.rose },
    { key: 'events', label: 'Events', icon: <FiUsers size={14} />, color: C.rose },
    { key: 'gifts', label: 'Gift Registry', icon: <FiGift size={14} />, color: C.gold },
    { key: 'vendors', label: 'Vendors', icon: <HiOutlineBuildingStorefront size={14} />, color: C.gold },
    { key: 'security', label: 'Security', icon: <FiShield size={14} />, color: C.rose },
  ];

  const filteredFaqs = useMemo(() => {
    let result = activeCategory === 'all' ? faqs : faqs.filter(f => f.category === activeCategory);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(f => f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q));
    }
    return result;
  }, [faqs, activeCategory, searchQuery]);

  const inputStyle = {
    width: '100%', padding: '13px 14px', borderRadius: '8px',
    border: `1px solid ${C.creamDark}`, fontSize: '14px',
    fontFamily: 'Inter, -apple-system, sans-serif',
    color: C.dark, background: C.white, outline: 'none',
    boxSizing: 'border-box', transition: 'border-color 0.2s',
  };

  return (
    <div style={{ minHeight: '100vh', background: C.cream, fontFamily: 'Inter, -apple-system, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        .help-input:focus { border-color: ${C.rose} !important; box-shadow: 0 0 0 3px ${C.roseLight}; }
      `}</style>

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)', borderBottom: `1px solid ${C.creamDark}`, padding: '14px 0' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to='/' style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: C.dark, fontSize: '14px', fontWeight: 500 }}>
            <FiArrowLeft size={16} />
            <img src="/ggr-logo-deck.png" alt='GGR' style={{ height: '50px', width: 'auto' }} />
          </Link>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Link to='/about' style={{ padding: '9px 16px', borderRadius: '8px', color: C.dark, textDecoration: 'none', fontSize: '13px', fontWeight: 500 }}>About</Link>
            <Link to='/contact' style={{ padding: '9px 16px', borderRadius: '8px', color: C.dark, textDecoration: 'none', fontSize: '13px', fontWeight: 500 }}>Contact</Link>
            <Link to='/register' style={{ padding: '9px 20px', borderRadius: '8px', background: C.rose, color: C.white, textDecoration: 'none', fontSize: '13px', fontWeight: 600 }}>Get Started</Link>
          </div>
        </div>
      </nav>

      {/* Hero with Search */}
      <section style={{ background: `linear-gradient(135deg, ${C.rose} 0%, ${C.roseDeep} 100%)`, padding: '64px 0 48px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: '-30%', right: '-10%', width: '500px', height: '500px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
          <div style={{ position: 'absolute', bottom: '-20%', left: '5%', width: '300px', height: '300px', borderRadius: '50%', background: `${C.gold}08` }} />
        </div>
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '0 28px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 16px', borderRadius: '20px', background: 'rgba(255,255,255,0.15)', color: C.white, fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '16px' }}>
            <FiHelpCircle size={12} /> Help & Support
          </div>
          <h1 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: 'clamp(30px, 4vw, 46px)', fontWeight: 700, color: C.white, lineHeight: 1.15, marginBottom: '14px' }}>
            How can we <span style={{ color: C.gold }}>help?</span>
          </h1>
          <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, maxWidth: '480px', margin: '0 auto 24px' }}>
            Find answers, get support, and reach out to our team. We're here to make your experience seamless.
          </p>

          {/* Search Bar */}
          <div style={{ maxWidth: '520px', margin: '0 auto', position: 'relative' }}>
            <FiSearch size={18} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: C.gray, pointerEvents: 'none' }} />
            <input
              type='text'
              placeholder='Search for answers...'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ width: '100%', padding: '14px 16px 14px 44px', borderRadius: '12px', border: 'none', fontSize: '15px', fontFamily: 'Inter, sans-serif', color: C.dark, background: C.white, outline: 'none', boxShadow: '0 4px 24px rgba(0,0,0,0.12)', boxSizing: 'border-box' }}
            />
          </div>
        </div>
      </section>

      {/* Quick Help Cards */}
      <section style={{ padding: '0 0 48px', marginTop: '-20px', position: 'relative', zIndex: 2 }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 28px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          {[
            { icon: <FiPhone />, title: 'Call / WhatsApp', value: '+27 61 391 1942', href: 'https://wa.me/27613911942', color: '#25D366' },
            { icon: <FiMail />, title: 'Email Us', value: 'support@grandgatherregistry.co.za', href: 'mailto:support@grandgatherregistry.co.za', color: C.rose },
            { icon: <FiMessageCircle />, title: 'WhatsApp', value: 'Quick response', href: 'https://wa.me/27613911942', color: C.gold },
          ].map((c, i) => (
            <a key={i} href={c.href || '#'} target={c.href?.startsWith('http') ? '_blank' : undefined} rel={c.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              style={{ padding: '20px 24px', background: C.white, borderRadius: '14px', border: `1px solid ${C.creamDark}`, textDecoration: 'none', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', display: 'block', transition: 'transform 0.2s' }}
              onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}>
              <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: `${c.color}12`, color: c.color, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontSize: '18px' }}>{c.icon}</div>
              <div style={{ fontSize: '11px', color: C.gray, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, marginBottom: '4px' }}>{c.title}</div>
              <div style={{ fontSize: '14px', color: C.dark, fontWeight: 600 }}>{c.value}</div>
            </a>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ padding: '0 0 40px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 28px' }}>
          <h2 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '28px', marginBottom: '8px', color: C.dark, textAlign: 'center' }}>
            Frequently Asked <span style={{ color: C.rose }}>Questions</span>
          </h2>
          <p style={{ fontSize: '14px', color: C.gray, marginBottom: '24px', textAlign: 'center' }}>
            {searchQuery ? `${filteredFaqs.length} result${filteredFaqs.length !== 1 ? 's' : ''} for "${searchQuery}"` : 'Quick answers to common questions'}
          </p>

          {/* Category Filter */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '24px' }}>
            {categories.map(cat => (
              <button key={cat.key} onClick={() => setActiveCategory(cat.key)} style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '20px',
                border: activeCategory === cat.key ? `1.5px solid ${C.rose}` : `1.5px solid ${C.creamDark}`,
                background: activeCategory === cat.key ? C.roseLight : C.white,
                color: activeCategory === cat.key ? C.rose : C.gray,
                fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s',
              }}>
                {cat.icon} {cat.label}
              </button>
            ))}
          </div>

          <div style={{ background: C.white, borderRadius: '16px', padding: '8px 28px', border: `1px solid ${C.creamDark}`, boxShadow: '0 2px 16px rgba(0,0,0,0.03)' }}>
            {filteredFaqs.length > 0 ? filteredFaqs.map(f => (
              <div key={f.id} style={{ borderBottom: `1px solid ${C.creamDark}` }}>
                <div onClick={() => setOpenFaq(openFaq === f.id ? null : f.id)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', padding: '18px 0' }}>
                  <span style={{ fontWeight: 600, fontSize: '15px', color: C.dark, flex: 1, paddingRight: '12px' }}>{f.question}</span>
                  <FiChevronDown size={18} style={{ color: C.gray, transition: 'transform 0.2s', transform: openFaq === f.id ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
                </div>
                {openFaq === f.id && <p style={{ fontSize: '14px', color: C.gray, lineHeight: 1.7, margin: '0 0 18px', paddingLeft: '4px' }}>{f.answer}</p>}
              </div>
            )) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: C.grayLight }}>
                <FiHelpCircle size={32} style={{ marginBottom: '10px', opacity: 0.3 }} />
                <p style={{ fontSize: '14px' }}>{searchQuery ? 'No results found. Try a different search term.' : 'FAQs loading...'}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Support Topics */}
      <section style={{ padding: '0 0 40px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 28px' }}>
          <h2 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '24px', marginBottom: '20px', color: C.dark, textAlign: 'center' }}>
            Browse by <span style={{ color: C.rose }}>Topic</span>
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
            {[
              { icon: <FiUsers size={22} />, title: 'Event Help', desc: 'Creating events, managing guests, RSVPs, and invitations', color: C.rose },
              { icon: <FiGift size={22} />, title: 'Gift Registry', desc: 'Setting up registries, contributions, banking details, and proof of payment', color: C.gold },
              { icon: <HiOutlineBuildingStorefront size={22} />, title: 'Vendor Support', desc: 'Vendor registration, quotes, profiles, and marketplace questions', color: C.rose },
              { icon: <FiShield size={22} />, title: 'Privacy & Security', desc: 'Account security, data protection, banking safety, and privacy', color: C.gold },
            ].map((t, i) => (
              <div key={i} style={{ padding: '24px', background: C.white, borderRadius: '14px', border: `1px solid ${C.creamDark}`, transition: 'transform 0.2s' }}
                onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: `${t.color}10`, color: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px' }}>{t.icon}</div>
                <h3 style={{ fontSize: '16px', fontWeight: 700, color: C.dark, marginBottom: '6px' }}>{t.title}</h3>
                <p style={{ fontSize: '13px', color: C.gray, lineHeight: 1.6 }}>{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry Form */}
      <section style={{ padding: '0 0 80px' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 28px' }}>
          <div style={{ background: C.white, borderRadius: '20px', padding: '36px', border: `1px solid ${C.creamDark}`, boxShadow: '0 2px 20px rgba(0,0,0,0.04)' }}>
            <h3 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '22px', marginBottom: '6px', color: C.dark, textAlign: 'center' }}>
              Still need help?
            </h3>
            <p style={{ fontSize: '14px', color: C.gray, marginBottom: '24px', textAlign: 'center' }}>Send us a message and we'll get back to you shortly</p>

            {enquirySubmitted ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: `${C.gold}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <FiCheck size={28} style={{ color: C.gold }} />
                </div>
                <h4 style={{ fontFamily: 'Playfair Display, Georgia, serif', fontSize: '20px', marginBottom: '8px', color: C.dark }}>Thank you!</h4>
                <p style={{ color: C.gray, fontSize: '14px', lineHeight: 1.6 }}>Your enquiry has been received. We'll get back to you shortly.</p>
                <button onClick={() => { setEnquirySubmitted(false); setEnquiryForm({ name: '', email: '', phone: '', subject: '', message: '' }); }}
                  style={{ marginTop: '20px', padding: '11px 22px', borderRadius: '8px', border: `1px solid ${C.creamDark}`, background: C.white, color: C.dark, cursor: 'pointer', fontWeight: 500, fontSize: '14px', fontFamily: 'Inter, sans-serif' }}>
                  Send Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '14px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: C.dark }}>Name *</label>
                    <input className='help-input' placeholder='Your Name' value={enquiryForm.name} onChange={e => setEnquiryForm({ ...enquiryForm, name: e.target.value })} required style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: C.dark }}>Email *</label>
                    <input className='help-input' placeholder='your@email.com' type='email' value={enquiryForm.email} onChange={e => setEnquiryForm({ ...enquiryForm, email: e.target.value })} required style={inputStyle} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: C.dark }}>Phone</label>
                    <input className='help-input' placeholder='+27 ...' value={enquiryForm.phone} onChange={e => setEnquiryForm({ ...enquiryForm, phone: e.target.value })} style={inputStyle} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: C.dark }}>Subject *</label>
                    <select className='help-input' value={enquiryForm.subject} onChange={e => setEnquiryForm({ ...enquiryForm, subject: e.target.value })} required style={{ ...inputStyle, color: enquiryForm.subject ? C.dark : C.gray }}>
                      <option value=''>Select subject</option>
                      <option value='Event Help'>Event Help</option>
                      <option value='Gift Registry'>Gift Registry</option>
                      <option value='Vendor Support'>Vendor Support</option>
                      <option value='Payment Issue'>Payment Issue</option>
                      <option value='Account & Security'>Account & Security</option>
                      <option value='Bug Report'>Bug Report</option>
                      <option value='Other'>Other</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: C.dark }}>Message *</label>
                  <textarea className='help-input' placeholder='Describe your issue...' rows={5} value={enquiryForm.message} onChange={e => setEnquiryForm({ ...enquiryForm, message: e.target.value })} required style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
                <button type='submit' disabled={loading} style={{ padding: '14px', borderRadius: '10px', border: 'none', background: C.rose, color: C.white, fontSize: '15px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: loading ? 0.6 : 1, boxShadow: `0 4px 12px ${C.rose}30` }}>
                  <FiSend size={16} /> {loading ? 'Sending...' : 'Submit'}
                </button>
              </form>
            )}
          </div>

          <a href='https://wa.me/27613911942' target='_blank' rel='noopener noreferrer'
            style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '20px 24px', background: '#25D366', borderRadius: '14px', textDecoration: 'none', color: C.white, marginTop: '20px', boxShadow: '0 4px 16px rgba(37,211,102,0.25)' }}>
            <FiMessageCircle size={24} />
            <div>
              <div style={{ fontWeight: 700, fontSize: '15px' }}>Prefer WhatsApp?</div>
              <div style={{ fontSize: '13px', opacity: 0.85 }}>Quick response · Mon–Fri 8am–6pm</div>
            </div>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: '60px 0 40px', background: C.dark, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '0 28px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '40px', marginBottom: '40px' }}>
            {/* Logo & Description */}
            <div style={{ maxWidth: '300px' }}>
              <img
                src="/ggr-logo-deck.png"
                alt="GGR"
                style={{ height: '70px', width: 'auto', filter: 'brightness(0) invert(1)', marginBottom: '16px' }}
              />
              <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, marginBottom: '16px' }}>
                South Africa's AI-powered event management platform. Plan weddings, lobola, birthdays and more with GiGi.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: C.white, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '1px' }}>Product</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['Features', 'How It Works', 'Pricing', 'Vendors'].map((item) => (
                  <li key={item} style={{ marginBottom: '10px' }}>
                    <Link to={`/#${item.toLowerCase().replace(' ', '-')}`} style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', transition: 'color 0.2s' }}>{item}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: C.white, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '1px' }}>Company</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['About', 'Contact', 'Careers', 'Partnerships'].map((item) => (
                  <li key={item} style={{ marginBottom: '10px' }}>
                    <Link to={`/${item.toLowerCase()}`} style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', transition: 'color 0.2s' }}>{item}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: C.white, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '1px' }}>Legal</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['Privacy Policy', 'Terms of Service', 'FAQs'].map((item) => (
                  <li key={item} style={{ marginBottom: '10px' }}>
                    {item === 'FAQs' ? (
                      <a href="/FAQs.pdf" download style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', transition: 'color 0.2s' }}>{item}</a>
                    ) : (
                      <Link to={`/${item.toLowerCase().replace(' ', '-')}`} style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', transition: 'color 0.2s' }}>{item}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div style={{ paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.35)', margin: 0 }}>
              &copy; 2026 Grand Gather Registry. Made with love in South Africa.
            </p>
            <div style={{ display: 'flex', gap: '16px' }}>
              {['Facebook', 'Instagram', 'TikTok'].map((social) => (
                <a key={social} href={`https://${social.toLowerCase()}.com/grandgatherregistry`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}>
                  {social}
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}